import { Card, CardContent, Typography, Divider } from '@mui/material';
import Grid from '@mui/material/Grid';
import type { EntityDetailContext, EntityDetailRenderer, EntityDetailTab } from '../types';
import { PersonHandReceiptsTab } from './PersonHandReceiptsTab';
import { usePersonLabels } from '@/hooks/usePersonLabels';

const hasValue = (value: unknown): boolean => value !== null && value !== undefined && value !== '';

/** Renders the individual ID label row using the configurable label from module config. */
const IndividualIdLabel: React.FC = () => {
  const { individualIdLabel } = usePersonLabels('front_window');
  return (
    <Typography variant="body2" color="text.secondary">
      {individualIdLabel}:
    </Typography>
  );
};

/**
 * Person-specific entity detail renderer.
 * Provides custom layouts for person entities with contact info and military details.
 */
export const createPersonDetailRenderer = (): EntityDetailRenderer => {
  return {
    renderTabs: (context: EntityDetailContext): EntityDetailTab[] => {
      const { entity } = context;

      const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return 'Invalid Date';
        }
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
      };

      return [
        {
          label: 'Contact Information',
          content: (
            <Grid container spacing={3}>
              {/* Personal Details */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Personal Details
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Name:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">
                          {[
                            entity.entity_data.first_name,
                            entity.entity_data.middle_name,
                            entity.entity_data.last_name,
                          ]
                            .map((value) => (hasValue(value) ? String(value) : ''))
                            .filter((value) => value.length > 0)
                            .join(' ') || 'N/A'}
                        </Typography>
                      </Grid>

                      {hasValue(entity.entity_data.individual_id) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <IndividualIdLabel />
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.individual_id || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {hasValue(entity.entity_data.email) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Email:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.email || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {hasValue(entity.entity_data.phone) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Phone:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.phone || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Military Information */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Military Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      {hasValue(entity.entity_data.rank) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Rank:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.rank || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {hasValue(entity.entity_data.unit) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Section:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.unit || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {hasValue(entity.entity_data.mos) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              MOS:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.mos || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {hasValue(entity.entity_data.billet) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Workforce Category:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.billet || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {hasValue(entity.entity_data.position) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Position:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.position || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {hasValue(entity.entity_data.service_branch) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Service Branch:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.service_branch || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {hasValue(entity.entity_data.component) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Component:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.component || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}

                      {hasValue(entity.entity_data.agency) && (
                        <>
                          <Grid size={{ xs: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                              Agency:
                            </Typography>
                          </Grid>
                          <Grid size={{ xs: 8 }}>
                            <Typography variant="body2">
                              {String(entity.entity_data.agency || '')}
                            </Typography>
                          </Grid>
                        </>
                      )}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ),
        },
        {
          label: 'Permanent Hand Receipt',
          content: <PersonHandReceiptsTab personId={entity.id} type="phr" />,
        },
        {
          label: 'Temporary Hand Receipt',
          content: <PersonHandReceiptsTab personId={entity.id} type="thr" />,
        },
        {
          label: 'Additional Information',
          content: (
            <Grid container spacing={3}>
              {/* System Information */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      eQuip System Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Entity ID:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                        >
                          {entity.id}
                        </Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Status:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{entity.status}</Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Version:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{entity.version}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Data Source */}
              {entity.metadata && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Data Source
                      </Typography>
                      <Divider sx={{ mb: 2 }} />

                      <Grid container spacing={2}>
                        {hasValue(entity.metadata.source) && (
                          <>
                            <Grid size={{ xs: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                Source:
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 8 }}>
                              <Typography variant="body2">
                                {entity.metadata.source === 'people-sync-api'
                                  ? 'PeopleSync Core API'
                                  : String(entity.metadata.source || '')}
                              </Typography>
                            </Grid>
                          </>
                        )}

                        {hasValue(entity.metadata.people_sync_synced_at) && (
                          <>
                            <Grid size={{ xs: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                Last Synced:
                              </Typography>
                            </Grid>
                            <Grid size={{ xs: 8 }}>
                              <Typography variant="body2">
                                {formatDate(String(entity.metadata.people_sync_synced_at || ''))}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Timestamps */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      eQuip Asset Management System
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Created in eQuip:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{formatDate(entity.created_at)}</Typography>
                      </Grid>

                      <Grid size={{ xs: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          Updated in eQuip:
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 8 }}>
                        <Typography variant="body2">{formatDate(entity.updated_at)}</Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ),
        },
      ];
    },
  };
};
